import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { Send, Menu, X, ThumbsUp, ThumbsDown, Copy, LogOut } from 'lucide-react';
import { MarkdownRenderer } from '../components/MarkdownRenderer';

interface Message {
  id: string;
  userMessage: string;
  aiResponse: string;
  createdAt: string;
  helpful?: boolean;
}

const MODES_BR = [
  { id: 'bndes-automatico', label: 'BNDES Automático', examples: ['Qual o prazo máximo do BNDES Automático?', 'Como funciona a estrutura 70/30?'] },
  { id: 'finame', label: 'FINAME', examples: ['O que é o CFI do BNDES?', 'FINAME aceita equipamentos usados?'] },
  { id: 'capital-giro', label: 'Capital de Giro', examples: ['Qual banco tem melhor linha de giro?', 'Como calcular a necessidade de capital?'] },
  { id: 'inovacao-finep', label: 'Inovação & Finep', examples: ['O que é subvenção econômica?', 'Como funciona o Inovacred Expresso?'] },
  { id: 'fomento-regional', label: 'Fomento Regional', examples: ['O que é o Desenvolve SP?', 'BNB atende quais estados?'] },
  { id: 'renovacao', label: 'Renovação de Máquinas', examples: ['Quando vale trocar a máquina?', 'Como calcular custo de manutenção x novo?'] },
  { id: 'incentivos', label: 'Incentivos Fiscais', examples: ['O que é a Lei do Bem?', 'Como funciona o ICMS diferido?'] },
  { id: 'mei-autonomo', label: 'MEI & Autônomo', examples: ['MEI pode acessar FINAME?', 'O que é o CrediAmigo?'] },
  { id: 'rural-agro', label: 'Rural & Agro', examples: ['Qual a diferença entre PRONAF e PRONAMP?', 'Como funciona o Plano Safra?'] },
  { id: 'documentacao', label: 'Documentação', examples: ['Quais documentos o banco pede?', 'O que é o fluxo de caixa projetado?'] },
  { id: 'historico', label: 'Histórico & Alertas', examples: ['Como acompanhar meu contrato?', 'Quando renegociar uma dívida?'] },
];

const MODES_EUA = [
  { id: 'eua-financing', label: '🇺🇸 SBA Loans', examples: ['O que é o SBA 7(a)?', 'Como acessar o SBA 504?'] },
  { id: 'eua-financing', label: '🇺🇸 SBIR / Grants', examples: ['O que é um grant federal?', 'Como aplicar para o SBIR?'] },
  { id: 'eua-financing', label: '🇺🇸 USDA / Rural', examples: ['O que é o FSA Loan?', 'USDA atende brasileiros nos EUA?'] },
  { id: 'eua-financing', label: '🇺🇸 CDFIs', examples: ['O que é uma CDFI?', 'Como acessar crédito para minoria?'] },
];

const WELCOME_MESSAGES: Record<string, string> = {
  'bndes-automatico': 'Olá! Sou especialista em BNDES Automático. Posso te orientar sobre financiamento de máquinas, equipamentos e capital de giro associado. Por onde quer começar?',
  'finame': 'Olá! Sou especialista em FINAME. Posso te orientar sobre financiamento exclusivo de máquinas e equipamentos nacionais cadastrados no CFI do BNDES.',
  'capital-giro': 'Olá! Sou especialista em capital de giro. Posso te orientar sobre linhas de crédito para fluxo de caixa nos bancos de fomento e comerciais.',
  'inovacao-finep': 'Olá! Sou especialista em inovação e Finep. Posso te orientar sobre subvenção econômica, Inovacred, FAPs estaduais e BNDES P&D.',
  'fomento-regional': 'Olá! Sou especialista em bancos de fomento regionais. Me diz em qual estado você está e qual é o seu setor — vou indicar os programas mais adequados.',
  'renovacao': 'Olá! Sou especialista em renovação de máquinas. Posso te ajudar a calcular o momento ideal de troca e estruturar o projeto para o banco.',
  'incentivos': 'Olá! Sou especialista em incentivos fiscais. Posso te orientar sobre ICMS diferido, IPI reduzido, Lei do Bem e regimes especiais estaduais.',
  'mei-autonomo': 'Olá! Sou especialista em crédito para MEI e profissionais liberais. Posso te orientar sobre microcrédito, cooperativas e linhas acessíveis para pequenos negócios.',
  'rural-agro': 'Olá! Sou especialista em crédito rural. Posso te orientar sobre PRONAF, PRONAMP, FINAME Agrícola e o Plano Safra vigente.',
  'documentacao': 'Olá! Sou especialista em documentação para financiamentos. Posso te ajudar a montar o dossiê completo antes de ir ao banco.',
  'historico': 'Olá! Posso te ajudar a acompanhar seus contratos de financiamento, planejar renovações e entender quando renegociar.',
  'eua-financing': 'Olá! Sou especialista em financiamento público americano para brasileiros. Posso te orientar sobre SBA, SBIR, USDA e CDFIs. Você já tem registro legal (LLC ou C-Corp) nos EUA?',
};

export default function Chat() {
  const { mode = 'bndes-automatico' } = useParams<{ mode: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'brasil' | 'eua'>(
    mode === 'eua-financing' ? 'eua' : 'brasil'
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentModeBr = MODES_BR.find(m => m.id === mode);
  const currentModeEua = MODES_EUA.find(m => m.id === mode);
  const currentMode = currentModeBr || currentModeEua || MODES_BR[0];
  const welcomeMsg = WELCOME_MESSAGES[mode] || WELCOME_MESSAGES['bndes-automatico'];

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    const fetchHistory = async () => {
      try {
        const data = await api.getChatHistory(mode);
        setMessages(data.conversations);
      } catch (error) {
        console.error('Failed to fetch history:', error);
      } finally {
        setIsLoadingHistory(false);
      }
    };
    fetchHistory();
  }, [user, navigate, mode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const userMessage = input;
    setInput('');
    setIsLoading(true);
    try {
      const response = await api.sendMessage(userMessage, mode);
      setMessages(prev => [...prev, {
        id: response.id,
        userMessage,
        aiResponse: response.message,
        createdAt: response.createdAt,
      }]);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleExample = (example: string) => {
    setInput(example);
    setSidebarOpen(false);
    inputRef.current?.focus();
  };

  const handleMarkHelpful = async (messageId: string, helpful: boolean) => {
    try {
      await api.markHelpful(messageId, helpful);
      setMessages(prev => prev.map(msg => msg.id === messageId ? { ...msg, helpful } : msg));
    } catch (error) { console.error(error); }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleModeChange = (modeId: string) => {
    setSidebarOpen(false);
    navigate(`/chat/${modeId}`);
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#f4f6f8', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 40 }} />
      )}

      <aside style={{
        width: 280, flexShrink: 0,
        background: '#f8f9fa', borderRight: '1px solid #e0e0e0',
        display: 'flex', flexDirection: 'column',
        overflowY: 'auto', zIndex: 50,
      }}>
        <div style={{ padding: '20px 16px 12px', borderBottom: '1px solid #e0e0e0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 700, color: '#059669', padding: 0 }}>
              Inteligência Financeira
            </button>
          </div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            {user?.fullName || user?.email}
          </div>
        </div>

        <div style={{ display: 'flex', padding: '10px 10px 0', gap: 6 }}>
          <button onClick={() => setActiveTab('brasil')} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: activeTab === 'brasil' ? '#059669' : '#e5e7eb', color: activeTab === 'brasil' ? '#fff' : '#6b7280' }}>
            🇧🇷 Brasil
          </button>
          <button onClick={() => setActiveTab('eua')} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: activeTab === 'eua' ? '#059669' : '#e5e7eb', color: activeTab === 'eua' ? '#fff' : '#6b7280' }}>
            🇺🇸 EUA
          </button>
        </div>

        <div style={{ padding: '12px', flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, paddingLeft: 4 }}>
            Modos
          </div>
          {(activeTab === 'brasil' ? MODES_BR : MODES_EUA).map((m, i) => (
            <a
              key={i}
              href={`/chat/${m.id}`}
              onClick={e => { e.preventDefault(); handleModeChange(m.id); }}
              style={{
                display: 'block', width: '100%', padding: '11px 14px', marginBottom: 5,
                border: mode === m.id ? 'none' : '1px solid #e5e7eb',
                borderRadius: 10, textAlign: 'left', cursor: 'pointer',
                fontSize: 15, background: mode === m.id ? '#059669' : '#fff',
                color: mode === m.id ? '#fff' : '#374151',
                fontWeight: mode === m.id ? 600 : 500,
                textDecoration: 'none',
                boxShadow: mode === m.id ? '0 2px 6px rgba(5,150,105,0.25)' : '0 1px 2px rgba(0,0,0,0.04)',
                transition: 'all 0.15s',
              }}
            >
              {m.label}
            </a>
          ))}
        </div>

        <div style={{ padding: '12px', borderTop: '1px solid #e0e0e0' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, paddingLeft: 4 }}>
            Experimente perguntar
          </div>
          {currentMode.examples.map((ex, i) => (
            <button key={i} onClick={() => handleExample(ex)}
              style={{ display: 'block', width: '100%', padding: '8px 10px', marginBottom: 6, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, textAlign: 'left', cursor: 'pointer', fontSize: 13, color: '#6b7280', lineHeight: 1.4 }}>
              "{ex}"
            </button>
          ))}
        </div>

        <div style={{ padding: '12px 16px', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={() => navigate('/pricing')} style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {user?.subscriptionTier === 'premium' ? '⭐ Premium' : 'Assinar'}
          </button>
          <button onClick={handleLogout} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <LogOut size={14} /> Sair
          </button>
        </div>
      </aside>

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', minWidth: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1f2937' }}>{currentMode.label}</div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>Inteligência Financeira · IA especializada</div>
          </div>
          <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 8, padding: '5px 12px', fontSize: 12, color: '#6b7280', cursor: 'pointer' }}>
            ← Dashboard
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '80%' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#059669', marginBottom: 4 }}>Inteligência Financeira</div>
            <div style={{ padding: '10px 14px', borderRadius: 12, borderBottomLeftRadius: 4, background: '#f3f4f6', border: '1px solid #e0e0e0', fontSize: 15, lineHeight: 1.7, color: '#1f2937' }}>
              {welcomeMsg}
            </div>
          </div>

          {messages.map((message) => (
            <div key={message.id} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '80%', alignSelf: 'flex-end' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 4 }}>Você</div>
                <div style={{ padding: '10px 14px', borderRadius: 12, borderBottomRightRadius: 4, background: '#059669', color: '#fff', fontSize: 15, lineHeight: 1.7 }}>
                  {message.userMessage}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '80%' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#059669', marginBottom: 4 }}>Inteligência Financeira</div>
                <div style={{ padding: '10px 14px', borderRadius: 12, borderBottomLeftRadius: 4, background: '#f3f4f6', border: '1px solid #e0e0e0' }}>
                  <MarkdownRenderer content={message.aiResponse} />
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 6, paddingLeft: 4 }}>
                  <button onClick={() => handleCopy(message.aiResponse)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                    <Copy size={11} /> Copiar
                  </button>
                  <button onClick={() => handleMarkHelpful(message.id, true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: message.helpful === true ? '#059669' : '#9ca3af', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                    <ThumbsUp size={11} /> Útil
                  </button>
                  <button onClick={() => handleMarkHelpful(message.id, false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: message.helpful === false ? '#dc2626' : '#9ca3af', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                    <ThumbsDown size={11} /> Não útil
                  </button>
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '80%' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#059669', marginBottom: 4 }}>Inteligência Financeira</div>
              <div style={{ padding: '10px 14px', borderRadius: 12, borderBottomLeftRadius: 4, background: '#f3f4f6', border: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#9ca3af', opacity: 0.5 }} />)}
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid #e0e0e0', background: '#fff', flexShrink: 0 }}>
          <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: 10 }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Digite sua pergunta..."
              disabled={isLoading}
              style={{ flex: 1, padding: '13px 18px', border: '1px solid #e0e0e0', borderRadius: 10, fontSize: 15, outline: 'none', background: '#f9fafb' }}
            />
            <button type="submit" disabled={isLoading || !input.trim()}
              style={{ padding: '13px 22px', background: input.trim() && !isLoading ? '#059669' : '#d1d5db', color: '#fff', border: 'none', borderRadius: 10, cursor: input.trim() && !isLoading ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 600 }}>
              <Send size={15} /> Enviar
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}